// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/pages/Operacional/Waves.tsx
//  Entregas → Separação por Wave (lista + montagem). Porte de FrmSeparacaoWave.
//  A lista filtra (busca/status/período), ordena e pagina NO SERVIDOR; filtros na URL.
// ════════════════════════════════════════════════════════════════════════════

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { keepPreviousData, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Plus, Layers, PackageCheck, Loader2, Search } from 'lucide-react';

import { waveService } from '@/features/operacional/services/waveService';
import { WAVE_STATUS } from '@/features/operacional/types';
import { getApiErrorMessage } from '@/lib/apiClient';
import { alternarOrdenacao, type DirecaoOrdenacao } from '@/lib/consultas';
import { useFiltrosNaUrl } from '@/hooks/useFiltrosNaUrl';
import { usePaginacaoServidor } from '@/hooks/usePaginacaoServidor';
import { BarraFiltros } from '@/components/BarraFiltros';
import { FiltroPeriodo } from '@/components/FiltroPeriodo';
import { PaginacaoRodape } from '@/components/PaginacaoRodape';
import { SortableTableHead } from '@/components/SortableTableHead';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const STATUS_COR: Record<number, string> = {
  0: 'bg-zinc-500/10 text-zinc-500', 1: 'bg-blue-500/10 text-blue-600',
  2: 'bg-amber-500/10 text-amber-600', 3: 'bg-emerald-500/10 text-emerald-600',
  4: 'bg-rose-500/10 text-rose-600',
};

function fdata(iso?: string | null) {
  return iso ? new Date(iso).toLocaleDateString('pt-BR') : '—';
}

type Campo = 'numero' | 'status' | 'criacao';

const PADROES = {
  busca: '',
  status: 'todos',
  dtInicio: '',
  dtFim: '',
  ordenarPor: 'criacao',
  direcao: 'desc',
  pagina: 1,
  tamanhoPagina: 20,
};

const Waves = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { filtros, setFiltros, limparFiltros, filtrosAtivos } = useFiltrosNaUrl(PADROES);
  const pag = usePaginacaoServidor({
    controlado: { pagina: filtros.pagina, tamanhoPagina: filtros.tamanhoPagina, onChange: setFiltros },
  });
  const consulta = {
    busca: filtros.busca, status: filtros.status, dtInicio: filtros.dtInicio, dtFim: filtros.dtFim,
    ordenarPor: filtros.ordenarPor, direcao: filtros.direcao as DirecaoOrdenacao,
  };
  const { data: paginado, isLoading, isFetching: atualizando } = useQuery({
    queryKey: ['waves', pag.pagina, pag.tamanhoPagina, consulta],
    queryFn: () => waveService.getAll(pag.pagina, pag.tamanhoPagina, consulta),
    placeholderData: keepPreviousData,
  });
  const waves = paginado?.itens ?? [];

  const ordenacao = { ordenarPor: filtros.ordenarPor, direcao: filtros.direcao as DirecaoOrdenacao };
  const cabecalho = {
    ...ordenacao,
    onOrdenar: (campo: Campo) => setFiltros(alternarOrdenacao(ordenacao, campo, campo === 'status' ? 'asc' : 'desc')),
  };

  const [open, setOpen] = useState(false);
  const [buscaDisp, setBuscaDisp] = useState('');
  const [selecionados, setSelecionados] = useState<Set<number>>(new Set());
  const [serpentina, setSerpentina] = useState(false);
  const [observacao, setObservacao] = useState('');

  const { data: disponiveis = [], isFetching } = useQuery({
    queryKey: ['waves-disponiveis'],
    queryFn: () => waveService.disponiveis(),
    enabled: open,
  });

  const create = useMutation({
    mutationFn: () => waveService.create({
      movDavIds: Array.from(selecionados), serpentina, observacao: observacao || null,
    }),
    onSuccess: (w) => {
      queryClient.invalidateQueries({ queryKey: ['waves'] });
      toast.success('Wave criada.');
      setOpen(false);
      navigate(`/operacional/waves/${w.id}`);
    },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  const toggle = (id: number) => setSelecionados((s) => {
    const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n;
  });

  const abrir = () => {
    setSelecionados(new Set()); setSerpentina(false); setObservacao(''); setBuscaDisp(''); setOpen(true);
  };

  // A lista de disponíveis já vem inteira do servidor: o filtro do diálogo é local (nº do DAV ou cliente).
  const disponiveisFiltrados = useMemo(() => {
    const termo = buscaDisp.trim().toLowerCase();
    if (!termo) return disponiveis;
    return disponiveis.filter((d) =>
      String(d.numeroDav).includes(termo) || (d.clienteNome ?? '').toLowerCase().includes(termo));
  }, [disponiveis, buscaDisp]);

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Layers className="h-6 w-6" /> Separação por Wave
          </h1>
          <p className="text-muted-foreground text-sm mt-0.5">
            Consolide vários pedidos numa coleta única (picking) e confira o empacotamento (packing).
          </p>
        </div>
        <Button onClick={abrir} className="gap-2"><Plus className="h-4 w-4" /> Nova Wave</Button>
      </div>

      <BarraFiltros
        busca={filtros.busca}
        onBuscar={(busca) => setFiltros({ busca })}
        placeholderBusca="Nº da wave ou observação..."
        filtrosAtivos={filtrosAtivos}
        onLimpar={limparFiltros}
      >
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Status</Label>
          <Select value={filtros.status} onValueChange={(status) => setFiltros({ status })}>
            <SelectTrigger className="h-9 w-[160px]" aria-label="Status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todos</SelectItem>
              {WAVE_STATUS.map((rotulo, i) => (
                <SelectItem key={i} value={String(i)}>{rotulo}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <FiltroPeriodo
          rotulo="Criação"
          inicio={filtros.dtInicio}
          fim={filtros.dtFim}
          onChange={({ inicio, fim }) => setFiltros({ dtInicio: inicio, dtFim: fim })}
        />
        {atualizando && !isLoading && (
          <Loader2 className="h-4 w-4 mb-2.5 animate-spin text-muted-foreground" aria-label="Atualizando" />
        )}
      </BarraFiltros>

      <Card>
        <CardContent className="pt-4">
          {isLoading ? (
            <p className="text-sm text-muted-foreground py-8 text-center">Carregando...</p>
          ) : waves.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8 text-center">
              {filtrosAtivos > 0 ? 'Nenhuma wave encontrada no filtro.' : 'Nenhuma wave criada.'}
            </p>
          ) : (
            <div className="rounded-md border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <SortableTableHead campo="numero" {...cabecalho} className="w-20">Nº</SortableTableHead>
                    <SortableTableHead campo="status" {...cabecalho} className="w-36">Status</SortableTableHead>
                    <TableHead className="w-24 text-right">Pedidos</TableHead>
                    <SortableTableHead campo="criacao" {...cabecalho}>Criada</SortableTableHead>
                    <TableHead>Separação</TableHead>
                    <TableHead>Observação</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {waves.map((w) => (
                    <TableRow key={w.id} className="cursor-pointer hover:bg-accent/50" onClick={() => navigate(`/operacional/waves/${w.id}`)}>
                      <TableCell className="font-medium">{w.id}</TableCell>
                      <TableCell><Badge variant="secondary" className={STATUS_COR[w.status]}>{WAVE_STATUS[w.status] ?? w.status}</Badge></TableCell>
                      <TableCell className="text-right">{w.qtdPedidos}</TableCell>
                      <TableCell className="text-sm">{fdata(w.dtCriacao)}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{fdata(w.dtInicioSeparacao)} – {fdata(w.dtFimSeparacao)}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{w.observacao ?? '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <PaginacaoRodape {...pag.rodape({ total: paginado?.total ?? 0, totalPaginas: paginado?.totalPaginas ?? 1 })} />

      {/* Nova wave: seleção de pedidos disponíveis */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><PackageCheck className="h-5 w-5" /> Nova Wave</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <p className="text-sm text-muted-foreground">
              Selecione os pedidos confirmados (não entregues) para consolidar na coleta.
            </p>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                value={buscaDisp}
                onChange={(e) => setBuscaDisp(e.target.value)}
                placeholder="Filtrar por nº do DAV ou cliente..."
                className="pl-9"
              />
            </div>
            <div className="rounded-md border max-h-72 overflow-y-auto">
              {isFetching ? (
                <p className="text-sm text-muted-foreground py-6 text-center">Carregando pedidos...</p>
              ) : disponiveisFiltrados.length === 0 ? (
                <p className="text-sm text-muted-foreground py-6 text-center">
                  {disponiveis.length === 0 ? 'Nenhum pedido disponível.' : 'Nenhum pedido encontrado no filtro.'}
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10" />
                      <TableHead className="w-20">Nº DAV</TableHead>
                      <TableHead>Cliente</TableHead>
                      <TableHead className="w-20 text-right">Itens</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {disponiveisFiltrados.map((d) => (
                      <TableRow key={d.movDavId} className="cursor-pointer" onClick={() => toggle(d.movDavId)}>
                        <TableCell><Checkbox checked={selecionados.has(d.movDavId)} onCheckedChange={() => toggle(d.movDavId)} /></TableCell>
                        <TableCell className="font-medium">{d.numeroDav || '—'}</TableCell>
                        <TableCell className="text-sm">{d.clienteNome || 'Consumidor final'}</TableCell>
                        <TableCell className="text-right text-sm">{d.qtdItens}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>

            <div className="flex items-center gap-2">
              <Checkbox id="serp" checked={serpentina} onCheckedChange={(v) => setSerpentina(!!v)} />
              <Label htmlFor="serp" className="font-normal cursor-pointer">Rota em serpentina (otimizar coleta)</Label>
            </div>
            <div className="space-y-1">
              <Label>Observação</Label>
              <Input value={observacao} onChange={(e) => setObservacao(e.target.value)} maxLength={200} />
            </div>
            <p className="text-sm text-muted-foreground">{selecionados.size} pedido(s) selecionado(s).</p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={() => create.mutate()} disabled={selecionados.size === 0 || create.isPending}>
              Criar Wave
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Waves;
